/**
 * ChartConfigurationPanel
 * Accordion UI for configuring grain (grouping) columns in the KPI One Pager table.
 * Animated expand/collapse, horizontal responsive grain selection using KPIFilterDropdown,
 * add/remove slots, apply/reset.
 *
 * • Receives all state through props
 * • Exposes callbacks only
 * • No Redux inside the component
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Animated, Platform, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { GrainKey, GRAIN_DIMENSIONS, MAX_GRAIN_COLUMNS } from '../../constants/onePagerConstants';
import { createChartConfigPanelStyles } from '../../styles/chartConfigurationPanelStyles';
import KPIFilterDropdown, { KPIFilterDropdownItem } from '../KPIFilterDropdown';
import { useTheme } from '../../../../theme/useTheme';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ChartConfigurationPanelProps {
	readonly isExpanded: boolean;
	readonly pendingGrains: readonly string[];
	readonly grainSlotCount: number;
	readonly grainSelections: readonly GrainKey[];
	readonly manualOverride: boolean;
	readonly onToggleExpand: () => void;
	readonly onGrainChange: (index: number, value: string) => void;
	readonly onAddSlot: () => void;
	readonly onRemoveSlot: (index: number) => void;
	readonly onApply: () => void;
	readonly onReset: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Convert GRAIN_DIMENSIONS to KPIFilterDropdownItem format */
const GRAIN_DROPDOWN_ITEMS: KPIFilterDropdownItem[] = GRAIN_DIMENSIONS.map((dim) => ({
	id: dim.key,
	displayText: dim.label,
}));

type IpadGridItem = { type: 'slot'; idx: number } | { type: 'add'; idx: number };

// ─── Component ────────────────────────────────────────────────────────────────

const ChartConfigurationPanel: React.FC<ChartConfigurationPanelProps> = ({
	isExpanded,
	pendingGrains,
	grainSlotCount,
	grainSelections,
	manualOverride,
	onToggleExpand,
	onGrainChange,
	onAddSlot,
	onRemoveSlot,
	onApply,
	onReset,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
	const styles = createChartConfigPanelStyles(isDark, colors);

	// ─── Animation ──────────────────────────────────────────────────────
	const animatedHeight = useRef(new Animated.Value(isExpanded ? 1 : 0)).current;
	const chevronRotation = useRef(new Animated.Value(isExpanded ? 1 : 0)).current;

	useEffect(() => {
		Animated.timing(animatedHeight, {
			toValue: isExpanded ? 1 : 0,
			duration: 250,
			useNativeDriver: false,
		}).start();

		Animated.timing(chevronRotation, {
			toValue: isExpanded ? 1 : 0,
			duration: 200,
			useNativeDriver: Platform.OS !== 'web',
		}).start();
	}, [isExpanded, animatedHeight, chevronRotation]);

	const bodyStyle = useMemo(() => ({
		opacity: animatedHeight,
		// Use a generous maxHeight so content is never clipped when slots wrap to multiple rows
		maxHeight: animatedHeight.interpolate({ inputRange: [0, 1], outputRange: [0, 1200] }),
	}), [animatedHeight]);

	const chevronStyle = useMemo(() => ({
		transform: [{ rotate: chevronRotation.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] }) }],
	}), [chevronRotation]);

	// ─── Device detection (iPad only) ──────────────────────────────────
	const { width: screenWidth, height: screenHeight } = useWindowDimensions();
	const isIpad = Platform.OS === 'ios'
		&& Math.min(screenWidth, screenHeight) >= 768
		&& Math.max(screenWidth, screenHeight) <= 1366;

	// ─── Derived state ──────────────────────────────────────────────────
	const committedCount = grainSelections.length;
	const canAddSlot = grainSlotCount < MAX_GRAIN_COLUMNS;
	const canRemoveSlot = grainSlotCount > 1;

	const hasPendingChanges = useMemo(() => {
		const pendingCommitted = pendingGrains.filter((v) => v !== '');
		if (pendingCommitted.length !== grainSelections.length) return true;
		return pendingCommitted.some((v, i) => v !== grainSelections[i]);
	}, [pendingGrains, grainSelections]);

	/** Set of grain keys already used (for disabling in other dropdowns) */
	const usedValues = useMemo(() => new Set(pendingGrains.filter((v) => v !== '')), [pendingGrains]);

	const slotIndices = useMemo(() => Array.from({ length: grainSlotCount }, (_, i) => i), [grainSlotCount]);
	const iPadGridItems = useMemo(() => {
		const slotItems: IpadGridItem[] = slotIndices.map((idx) => ({ type: 'slot' as const, idx }));
		if (canAddSlot) {
			slotItems.push({ type: 'add', idx: -1 });
		}
		return slotItems;
	}, [slotIndices, canAddSlot]);

	// ─── Slot change handler ────────────────────────────────────────────
	const handleSlotChange = useCallback((index: number) => (selected: KPIFilterDropdownItem[]) => {
		if (selected.length === 0) {
			// Clear was pressed
			onGrainChange(index, '');
		} else {
			onGrainChange(index, String(selected[0].id));
		}
	}, [onGrainChange]);

	// ─── Render ─────────────────────────────────────────────────────────
	return (
		<View style={styles.container}>
			{/* Accordion Header */}
			<TouchableOpacity
				style={[styles.header, isExpanded && styles.headerExpanded]}
				onPress={onToggleExpand}
				activeOpacity={0.7}
				accessibilityLabel="Toggle Chart Configuration"
				accessibilityRole="button"
				accessibilityState={{ expanded: isExpanded }}
			>
				<View style={styles.headerLeft}>
					<MaterialIcons name="tune" size={18} color="#005CBC" style={styles.headerIcon} />
					<Text style={styles.headerTitle}>Chart Configuration</Text>
					<View style={styles.headerBadge}>
						<Text style={styles.headerBadgeText}>{committedCount} / {MAX_GRAIN_COLUMNS}</Text>
					</View>
				</View>
				<View style={styles.headerRight}>
					<Animated.View style={chevronStyle}>
						<MaterialIcons name="keyboard-arrow-down" size={22} color="#585858" />
					</Animated.View>
				</View>
			</TouchableOpacity>

			{/* Animated Body */}
			<Animated.View style={[bodyStyle, { overflow: 'hidden' }]}>
				<View style={styles.body}>
					<Text style={styles.description}>
						Select grain columns to group data. Values cannot repeat across slots.
					</Text>

					{/* Horizontal row of dropdowns */}
					{isIpad ? (
						// iPad: explicit rows of 4 to avoid React Native flexWrap height-measurement issues
						<View style={styles.slotsContainerColumn}>
							{Array.from({ length: Math.ceil(iPadGridItems.length / 4) }, (_, rowIdx) => {
								const rowItems = iPadGridItems.slice(rowIdx * 4, rowIdx * 4 + 4);
								return (
									<View
										key={`row-${rowIdx}`}
										style={[styles.slotsRow, rowIdx > 0 && styles.slotsRowSpaced]}
									>
										{rowItems.map((item, itemPos) => {
											if (item.type === 'add') {
												return (
													<View
														key="slot-add"
														style={[
															styles.slotItemIpad,
															itemPos < rowItems.length - 1 && styles.slotItemIpadSpaced,
														]}
													>
														<TouchableOpacity
															style={[styles.addButton, styles.addButtonInlineIpad]}
															onPress={onAddSlot}
															activeOpacity={0.7}
															accessibilityLabel="Add grain column"
														>
															<MaterialIcons name="add" size={16} color="#005CBC" />
															<Text style={styles.addButtonText}>Add Column</Text>
														</TouchableOpacity>
													</View>
												);
											}

											const idx = item.idx;
											const currentValue = pendingGrains[idx] ?? '';
											const selectedItem = currentValue
												? GRAIN_DROPDOWN_ITEMS.filter((item) => item.id === currentValue)
												: [];
											const disabledIds = new Set<string>();
											usedValues.forEach((v) => {
												if (v !== currentValue) disabledIds.add(v);
											});
											return (
												<View
													key={`slot-${idx}`}
													style={[
														styles.slotItemIpad,
														itemPos < rowItems.length - 1 && styles.slotItemIpadSpaced,
													]}
												>
													<View style={styles.slotHeaderRow}>
														<Text style={styles.slotHeaderLabel}>{`Column ${idx + 1}`}</Text>
														<TouchableOpacity
															style={[styles.removeButton, !canRemoveSlot && styles.removeButtonDisabled]}
															onPress={() => onRemoveSlot(idx)}
															disabled={!canRemoveSlot}
															activeOpacity={0.6}
															accessibilityLabel={`Remove column ${idx + 1}`}
														>
															<MaterialIcons name="close" size={12} color={canRemoveSlot ? '#838383' : '#C8C8C8'} />
														</TouchableOpacity>
													</View>
													<KPIFilterDropdown
														childList={GRAIN_DROPDOWN_ITEMS}
														selectedValue={selectedItem}
														setSelectedValue={handleSlotChange(idx)}
														placeholder="Select dimension..."
														singleSelect={true}
														sortItems={false}
														disabledIds={disabledIds}
														style={[styles.slotDropdown, styles.slotDropdownIpad]}
													/>
												</View>
											);
										})}
									</View>
								);
							})}
						</View>
					) : (
						// Desktop / non-iPad
						<View style={styles.slotsContainer}>
							{slotIndices.map((idx) => {
								const currentValue = pendingGrains[idx] ?? '';
								const selectedItem = currentValue
									? GRAIN_DROPDOWN_ITEMS.filter((item) => item.id === currentValue)
									: [];

								// Disable items that are already used in other slots
								const disabledIds = new Set<string>();
								usedValues.forEach((v) => {
									if (v !== currentValue) {
										disabledIds.add(v);
									}
								});

								return (
									<View key={`slot-${idx}`} style={styles.slotItem}>
										<View style={styles.slotHeaderRow}>
											<Text style={styles.slotHeaderLabel}>{`Column ${idx + 1}`}</Text>
											<TouchableOpacity
												style={[styles.removeButton, !canRemoveSlot && styles.removeButtonDisabled]}
												onPress={() => onRemoveSlot(idx)}
												disabled={!canRemoveSlot}
												activeOpacity={0.6}
												accessibilityLabel={`Remove column ${idx + 1}`}
											>
												<MaterialIcons name="close" size={12} color={canRemoveSlot ? '#838383' : '#C8C8C8'} />
											</TouchableOpacity>
										</View>
										<KPIFilterDropdown
											childList={GRAIN_DROPDOWN_ITEMS}
											selectedValue={selectedItem}
											setSelectedValue={handleSlotChange(idx)}
											placeholder="Select dimension..."
											singleSelect={true}
											sortItems={false}
											disabledIds={disabledIds}
											style={styles.slotDropdown}
										/>
									</View>
								);
							})}

							{/* Add Column button inline */}
							{canAddSlot && (
								<TouchableOpacity
									style={styles.addButton}
									onPress={onAddSlot}
									activeOpacity={0.7}
									accessibilityLabel="Add grain column"
								>
									<MaterialIcons name="add" size={16} color="#005CBC" />
									<Text style={styles.addButtonText}>Add Column</Text>
								</TouchableOpacity>
							)}
						</View>
					)}

					{/* Action Buttons */}
					<View style={[styles.actionsRow, isIpad && styles.actionsRowIpad]}>
						<View style={styles.spacer} />

						{manualOverride && (
							<TouchableOpacity
								style={styles.resetButton}
								onPress={onReset}
								activeOpacity={0.7}
								accessibilityRole="button"
								accessibilityLabel="Reset grain configuration"
							>
								<Text style={styles.resetButtonText}>Reset</Text>
							</TouchableOpacity>
						)}

						<TouchableOpacity
							style={[styles.applyButton, !hasPendingChanges && styles.applyButtonDisabled]}
							onPress={onApply}
							disabled={!hasPendingChanges}
							activeOpacity={0.7}
							accessibilityRole="button"
							accessibilityLabel="Apply grain configuration"
						>
							<Text style={styles.applyButtonText}>Apply</Text>
						</TouchableOpacity>
					</View>
				</View>
			</Animated.View>
		</View>
	);
};

ChartConfigurationPanel.displayName = 'ChartConfigurationPanel';

export default React.memo(ChartConfigurationPanel);
